import { gql, useMutation, useQuery } from '@apollo/client';
import {
	Mutation,
	MutationOrganizationProfileUpdateArgs,
	OrganizationProfileUpdate,
	Query,
	QueryOrganizationProfileArgs,
} from 'middleware-types';
import { PageError } from 'utils/errors';

export enum BusinessLines {
	AdjustingFirm = 'AdjustingFirm',
	CatCompany = 'CatCompany',
	IndependentAdjuster = 'IndependentAdjuster',
	PublicAdjuster = 'PublicAdjuster',
	ManagingGeneralAgent = 'ManagingGeneralAgent',
	ClaimSupport = 'ClaimSupport',
	ClaimsServicesProvider = 'ClaimsServicesProvider',
	InsuranceCarrier = 'InsuranceCarrier',
	Contractor = 'Contractor',
	MitigationCompany = 'MitigationCompany',
	RoofingCompany = 'RoofingCompany',
	ExpertServices = 'ExpertServices',
	MiscellaneousServiceProvider = 'MiscellaneousServiceProvider',
	Tpa = 'Tpa',
	Other = 'Other',
}

/**
 * useOrgProfileHeaderQuery(userId) - Hook to get the org profile header section.
 *
 * @param {string} userId
 * @return {*}
 */
export const useOrgProfileHeaderQuery = (organizationId: string) => {
	const { loading, data, error } = useQuery<
		Pick<Query, 'organizationProfile'>,
		QueryOrganizationProfileArgs
	>(
		gql`
			query orgProfile($organizationId: ID!) {
				organizationProfile(organizationId: $organizationId) {
					id
					displayName
					displayLocation
					about
					websiteUrl
					connection {
						id
						state
						requestingUserId
						requestingOrganizationId
						targetUserId
						requestingUserId
						requestingUserEmblem {
							id
							displayName
						}
					}
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
			variables: {
				organizationId,
			},
		}
	);

	// Any errors on this modal should be thrown to be caught at the error boundary.
	if (error) {
		throw new PageError([error].filter((p) => p !== undefined));
	}

	return { profile: data?.organizationProfile, loading };
};

/**
 * Hook to use the org profile header mutation.
 * @param organizationId
 * @returns
 */
export const useOrgProfileHeaderMutation = (organizationId: string) => {
	const [updateOrgProfileMut, { error }] = useMutation<
		Pick<Mutation, 'organizationProfileUpdate'>,
		MutationOrganizationProfileUpdateArgs
	>(
		gql`
			mutation organizationProfileUpdate(
				$organizationId: ID!
				$organizationProfile: OrganizationProfileUpdate!
			) {
				organizationProfileUpdate(
					organizationId: $organizationId
					organizationProfile: $organizationProfile
				) {
					id
					displayName
				}
			}
		`,
		{
			errorPolicy: 'all',
			update: (cache) => {
				// Updating the organization header can change the displayName on account, search, and emblem.
				cache.evict({
					id: `OrganizationAccount:${organizationId}`,
					fieldName: 'displayName',
				});
				cache.evict({
					id: `OrganizationEmblem:${organizationId}`,
					fieldName: 'displayName',
				});
				cache.evict({
					id: `QuickSearchEntity:${organizationId}`,
					fieldName: 'displayName',
				});

				cache.gc();
			},
		}
	);

	const updateOrgProfile = async (organizationProfile: OrganizationProfileUpdate) => {
		return updateOrgProfileMut({
			variables: {
				organizationId,
				organizationProfile,
			},
		});
	};

	return { updateOrgProfile, error };
};
